import React, { FC, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'
import { IAppState } from 'infra/AppState'
import { API_RESPONSE_TYPE } from 'shared/constants'
import { broadcastEvent } from 'state/dashboard/dashboard.actions'
import { addAutoFadeNotification } from 'pages/Notification/Notification.actions'
import { NotificationType } from 'pages/Notification/Notification.types'
import { requestPinOtp, verifyPinOtp } from 'services/Api/SecurityPinApi'
import { updateIndividualUser } from 'services/Api/IdentityApi'
import {
  OTP_LENGTH,
  PIN_LENGTH,
  hasLocalPin,
  isPinSet,
  savePin,
  syncPin,
  verifyPin
} from 'services/SecurityPin/securityPin'
import { SecurityPinRequest, registerSecurityPinHost } from './requireSecurityPin'
import { SecurityPinHostStyles } from './SecurityPinHost.styles'

// loading → enter (PIN is set) | prompt (no PIN yet) → otp → create → confirm
type Step = 'loading' | 'enter' | 'prompt' | 'otp' | 'create' | 'confirm' | 'offline'

const RESEND_AFTER_SECONDS = 30
const SYNC_WAIT_MS = 3000

const LockGlyph: FC = () => (
  <svg width={26} height={26} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M18 8h-1V6A5 5 0 0 0 7 6v2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V10a2 2 0 0 0-2-2zm-6 9a2 2 0 1 1 0-4 2 2 0 0 1 0 4zm3.1-9H8.9V6a3.1 3.1 0 0 1 6.2 0v2z" />
  </svg>
)
const CloseGlyph: FC = () => (
  <svg width={20} height={20} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M19 6.41 17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
)

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

// The Security PIN dialog (Android/iOS Enter PIN → Forgot PIN / Set PIN prompt → OTP → Set
// PIN). Mounted once; opened through requireSecurityPin / changeSecurityPin.
export const SecurityPinHost: FC = () => {
  const classes = SecurityPinHostStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const individualUser = useSelector(
    (state: IAppState) => state.Dashboard.businessAccounts?.individualUser
  )
  const [request, setRequest] = useState<SecurityPinRequest | null>(null)
  const [step, setStep] = useState<Step>('loading')
  const [value, setValue] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [otpId, setOtpId] = useState('')
  const [resendIn, setResendIn] = useState(0)
  // The PIN typed on the "create" step, checked against the "confirm" step
  const firstPinRef = useRef('')
  // Whether the user already had a PIN when this run started (Set vs Changed analytics)
  const hadPinRef = useRef(false)
  // Background refresh of the checksum, awaited only when a PIN does not match
  const syncRef = useRef<Promise<void>>(Promise.resolve())
  // Guards async continuations against a dialog that was closed or reopened meanwhile
  const runRef = useRef(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const track = (event: string, props: Record<string, unknown> = {}) =>
    dispatch(broadcastEvent(event, { Flow: request?.flow, ...props }))

  const goTo = (next: Step) => {
    setValue('')
    setError('')
    setBusy(false)
    setStep(next)
  }

  const close = () => {
    runRef.current += 1
    setRequest(null)
  }

  const finish = () => {
    const done = request?.onSuccess
    close()
    if (done) {
      done()
    }
  }

  const sendOtp = async () => {
    const run = runRef.current
    const mobile = individualUser?.mobile
    setError('')
    if (!mobile) {
      setError(t('dashboard:Something went wrong. Please try again.'))
      return
    }
    setBusy(true)
    const response = await requestPinOtp(mobile)
    if (run !== runRef.current) {
      return
    }
    setBusy(false)
    if (response.type === API_RESPONSE_TYPE.SUCCESS && response.data?.otp_id) {
      setOtpId(response.data.otp_id)
      setResendIn(RESEND_AFTER_SECONDS)
    } else {
      setError(
        response.data?.response?.status === 429
          ? t('dashboard:PinTooManyAttempts')
          : t('dashboard:Something went wrong. Please try again.')
      )
    }
  }

  const startOtp = () => {
    setOtpId('')
    goTo('otp')
    sendOtp()
  }

  // A new request: decide where the dialog starts
  useEffect(() => {
    registerSecurityPinHost((next) => {
      runRef.current += 1
      const run = runRef.current
      firstPinRef.current = ''
      hadPinRef.current = hasLocalPin()
      syncRef.current = hadPinRef.current ? syncPin() : Promise.resolve()
      setRequest(next)
      setValue('')
      setError('')
      setBusy(false)
      setOtpId('')
      setStep('loading')
      isPinSet()
        .then((set) => {
          if (run !== runRef.current) {
            return
          }
          hadPinRef.current = set
          if (next.mode === 'change') {
            // Security page: straight to OTP → new PIN, the old PIN is never asked (mobile)
            setStep('otp')
          } else {
            setStep(set ? 'enter' : 'prompt')
          }
        })
        .catch(() => {
          if (run === runRef.current) {
            setStep('offline')
          }
        })
    })
    return () => registerSecurityPinHost(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 'change' mode lands on the OTP step without going through startOtp
  useEffect(() => {
    if (request && step === 'otp' && !otpId && !busy && !error) {
      sendOtp()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, request])

  useEffect(() => {
    if (step !== 'otp' || resendIn <= 0) {
      return undefined
    }
    const timer = setTimeout(() => setResendIn((seconds) => seconds - 1), 1000)
    return () => clearTimeout(timer)
  }, [step, resendIn])

  useEffect(() => {
    if (request && !busy) {
      inputRef.current?.focus()
    }
  }, [request, step, busy])

  // Escape closes this dialog only (capture phase: the drawer underneath stays open)
  useEffect(() => {
    if (!request) {
      return undefined
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        close()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request])

  const onEnterComplete = async (pin: string) => {
    const run = runRef.current
    setBusy(true)
    let ok = await verifyPin(pin)
    if (!ok) {
      // The PIN may have been changed on the phone: let the sync land, then look again
      await Promise.race([syncRef.current, wait(SYNC_WAIT_MS)])
      ok = await verifyPin(pin)
    }
    if (run !== runRef.current) {
      return
    }
    if (ok) {
      track('Security Pin match')
      finish()
    } else {
      track('Security Pin Mismatch')
      setBusy(false)
      setValue('')
      setError(t('dashboard:PinIncorrect'))
    }
  }

  const onOtpComplete = async (otp: string) => {
    const run = runRef.current
    setBusy(true)
    const response = await verifyPinOtp(otpId, otp)
    if (run !== runRef.current) {
      return
    }
    if (response.type === API_RESPONSE_TYPE.SUCCESS) {
      goTo('create')
      return
    }
    const status = response.data?.response?.status
    const body = JSON.stringify(response.data?.response?.data || '')
    setBusy(false)
    setValue('')
    setError(
      status === 429
        ? t('dashboard:PinTooManyAttempts')
        : body.includes('otp_expired')
        ? t('dashboard:PinOtpExpired')
        : body.includes('invalid_otp') || status === 400 || status === 401
        ? t('dashboard:PinInvalidOtp')
        : t('dashboard:Something went wrong. Please try again.')
    )
  }

  const onConfirmComplete = async (pin: string) => {
    if (pin !== firstPinRef.current) {
      firstPinRef.current = ''
      goTo('create')
      setError(t('dashboard:PinMismatch'))
      return
    }
    const run = runRef.current
    setBusy(true)
    try {
      await savePin(pin)
    } catch {
      if (run === runRef.current) {
        firstPinRef.current = ''
        goTo('create')
        setError(t('dashboard:Something went wrong. Please try again.'))
      }
      return
    }
    // Mobile marks the account as using the 4-digit PIN; best effort
    if (individualUser?.id) {
      updateIndividualUser({
        individual_user_id: individualUser.id,
        individual_user: {
          user: { id: individualUser.id, mobile: individualUser.mobile },
          four_digit_pin_in: true
        },
        update_four_digit_pin_opt_in: true
      }).catch(() => undefined)
    }
    if (run !== runRef.current) {
      return
    }
    track(hadPinRef.current ? 'Security Pin Changed' : 'Security Pin Set')
    dispatch(
      addAutoFadeNotification({
        type: NotificationType.SUCCESS,
        bodyText: t('dashboard:PinUpdated')
      })
    )
    // A PIN just set counts as proven: the protected action runs without asking again
    finish()
  }

  const isOtp = step === 'otp'
  const length = isOtp ? OTP_LENGTH : PIN_LENGTH

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value.replace(/\D/g, '').slice(0, length)
    setValue(next)
    if (error) {
      setError('')
    }
    if (next.length !== length) {
      return
    }
    if (step === 'enter') {
      onEnterComplete(next)
    } else if (step === 'otp') {
      if (otpId) {
        onOtpComplete(next)
      }
    } else if (step === 'create') {
      firstPinRef.current = next
      goTo('confirm')
    } else if (step === 'confirm') {
      onConfirmComplete(next)
    }
  }

  if (!request || typeof document === 'undefined') {
    return null
  }

  const title =
    step === 'enter'
      ? t('dashboard:PinEnterTitle')
      : step === 'otp'
      ? t('dashboard:PinVerifyOtp')
      : step === 'confirm'
      ? t('dashboard:PinConfirmTitle')
      : t('dashboard:PinSetTitle')
  const subtitle =
    step === 'prompt'
      ? t('dashboard:PinSetupDescription')
      : step === 'otp'
      ? t('dashboard:PinOtpSentTo', { mobile: individualUser?.mobile || '' })
      : step === 'create'
      ? t('dashboard:PinSetHint')
      : step === 'confirm'
      ? t('dashboard:PinConfirmHint')
      : step === 'offline'
      ? t('dashboard:No internet connection')
      : ''
  const hasInput = step === 'enter' || step === 'otp' || step === 'create' || step === 'confirm'

  return createPortal(
    <div className={classes.scrim}>
      {/* role="dialog": drawers underneath stay open and dark mode lifts the card */}
      <div className={classes.card} role="dialog" aria-modal="true" aria-label={title}>
        <button
          type="button"
          className={classes.close}
          onClick={close}
          aria-label={t('dashboard:Close')}
          title={t('dashboard:Close')}
        >
          <CloseGlyph />
        </button>
        <span className={classes.lock}>
          <LockGlyph />
        </span>
        {step === 'loading' ? (
          <span className={classes.spinner} aria-live="polite" />
        ) : (
          <>
            <h5 className={classes.title}>{title}</h5>
            {subtitle ? <p className={classes.subtitle}>{subtitle}</p> : null}
          </>
        )}
        {hasInput ? (
          <>
            <input
              ref={inputRef}
              // A fresh input per step, so nothing typed carries over
              key={step}
              className={classNames(classes.code, {
                [classes.masked]: !isOtp,
                [classes.codeError]: Boolean(error)
              })}
              type="text"
              inputMode="numeric"
              autoComplete={isOtp ? 'one-time-code' : 'off'}
              maxLength={length}
              value={value}
              onChange={onChange}
              disabled={busy}
              aria-label={title}
              aria-invalid={Boolean(error)}
            />
            <p className={classes.error} role="alert">
              {error}
            </p>
          </>
        ) : null}
        {step === 'enter' ? (
          <button
            type="button"
            className={classes.link}
            onClick={() => {
              track('Forgot Password click')
              startOtp()
            }}
          >
            {t('dashboard:PinForgot')}
          </button>
        ) : null}
        {step === 'otp' ? (
          <button
            type="button"
            className={classes.link}
            disabled={busy || resendIn > 0}
            onClick={sendOtp}
          >
            {resendIn > 0
              ? t('auth:Resend Otp In', { secondsLeft: resendIn })
              : t('auth:Resend Otp')}
          </button>
        ) : null}
        {step === 'prompt' ? (
          <div className={classes.buttons}>
            <button type="button" className={classes.secondary} onClick={close}>
              {t('dashboard:Cancel')}
            </button>
            <button type="button" className={classes.primary} onClick={startOtp}>
              {t('dashboard:PinSetCta')}
            </button>
          </div>
        ) : null}
        {step === 'offline' ? (
          <div className={classes.buttons}>
            <button type="button" className={classes.primary} onClick={close}>
              {t('dashboard:Close')}
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  )
}

export default SecurityPinHost
